import { ProjectStatus, SupplierApprovalStatus } from '@/models/common/ProjectStatus';
import { SupplierFullDtoIn } from '@/models/in/SupplierFullDtoIn';

/**
 * Which status tab an item belongs to, on the coordinator's projects and suppliers lists
 * (HAT-1105, 22/10/2026). In a file of its own, away from the tabs component: a file that exports
 * both a component and plain functions loses Vite's Fast Refresh.
 *
 * "הכל" comes first and is the default, so the list opens exactly as it did before the tabs
 * existed - and a project the coordinator has just submitted, which is always waiting for
 * approval, is in it.
 */
export type ApprovalTab = 'all' | 'pending' | 'approved' | 'rejected';

export const APPROVAL_TAB_ORDER: ApprovalTab[] = ['all', 'pending', 'approved', 'rejected'];

/**
 * The three review sub-stages of a project fold into one "ממתין לאישור", the same aggregate the
 * status column already shows: a coordinator does not need to know which committee holds it.
 * Anything unknown is read as waiting - the reading that does not promise it is approved.
 */
export const projectApprovalTab = (status: ProjectStatus): Exclude<ApprovalTab, 'all'> => {
  if (status === ProjectStatus.Approved) return 'approved';
  if (status === ProjectStatus.Rejected) return 'rejected';
  return 'pending';
};

export const supplierApprovalTab = (status: SupplierApprovalStatus | undefined): Exclude<ApprovalTab, 'all'> => {
  if (status === SupplierApprovalStatus.Approved) return 'approved';
  if (status === SupplierApprovalStatus.Rejected) return 'rejected';
  // Suppliers that predate the approval feature are grandfathered as Approved by the server, so a
  // missing status is not "approved" - it is a response from a server that has not sent one, and
  // the safe reading is the one that does not promise the supplier can be paid.
  return 'pending';
};

/**
 * A supplier's status on the fund's suppliers list (SPR-10302). The office approves a supplier per
 * project, so for a supplier on the fund's projects it is read from those links: waiting while any
 * project still waits for the office, approved once it is approved for at least one and none waits,
 * rejected only when every project rejected it. A supplier the office rejected outright stays
 * rejected; one on no project (only requests were paid to it) keeps its own status.
 */
export const fundSupplierStatus = (
  supplier: Pick<SupplierFullDtoIn, 'approvalStatus' | 'projectApprovals'>
): SupplierApprovalStatus | undefined => {
  if (supplier.approvalStatus === SupplierApprovalStatus.Rejected) return SupplierApprovalStatus.Rejected;
  const links = supplier.projectApprovals ?? [];
  if (links.length === 0) return supplier.approvalStatus;
  if (links.some((link) => link.approvalStatus === SupplierApprovalStatus.PendingApproval)) {
    return SupplierApprovalStatus.PendingApproval;
  }
  if (links.some((link) => link.approvalStatus === SupplierApprovalStatus.Approved)) return SupplierApprovalStatus.Approved;
  return SupplierApprovalStatus.Rejected;
};

/** How many items sit in each tab, for the little numbers beside the labels. */
export const countByTab = <T,>(items: T[], tabOf: (item: T) => Exclude<ApprovalTab, 'all'>): Record<ApprovalTab, number> => {
  const counts: Record<ApprovalTab, number> = { all: items.length, pending: 0, approved: 0, rejected: 0 };
  for (const item of items) counts[tabOf(item)] += 1;
  return counts;
};
